import type { Field as FieldType } from "@types/Field";
import { operations } from "@types/Field";
import type { FormFieldProps } from "@types/FormFieldProps";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "./hooks/useForm";
import RenderFieldWithContext from "./RenderFieldWithContext";

const areConditionsMet = (
  field: FieldType,
  values: Record<string, any>,
): boolean => {
  if (!field.condition && !field.conditionsAnd) return true;

  if (field.condition) {
    if (Array.isArray(field.condition)) {
      return field.condition.some((cond) => {
        const { field: fieldName, value, operator = "===" } = cond;
        return operations[operator]?.(values[fieldName], value) ?? false;
      });
    }

    const { field: fieldName, value, operator = "===" } = field.condition;
    return operations[operator]?.(values[fieldName], value) ?? false;
  }

  if (field.conditionsAnd && Array.isArray(field.conditionsAnd)) {
    return field.conditionsAnd.every((cond) => {
      const { field: fieldName, value, operator = "===" } = cond;
      return operations[operator]?.(values[fieldName], value) ?? false;
    });
  }

  return false;
};

const FormFieldWithContext: React.FC<FormFieldProps> = ({
                                                          field,
                                                          errors,
                                                          values,
                                                          index,
                                                          touched,
                                                          hasSubmitted = false,
                                                        }: FormFieldProps): JSX.Element | null => {
  const { formData, updateFormData } = useForm();
  const { data } = formData;

  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);

  const isCurrentlyVisible = useMemo(
    () => areConditionsMet(field, values),
    [field, values],
  );

  const wasVisibleRef = useRef<boolean>(isCurrentlyVisible);
  const didInitCollapsedRef = useRef(false);

  const getFieldsAboveHeading = useCallback(
    (fieldIndex: number): FieldType[] => {
      if (!data?.fields) return [];

      const fields = data.fields.slice(fieldIndex + 1);
      const nextTitleIndex = fields.findIndex(
        (formField: FieldType) => formField.type === "title",
      );

      return nextTitleIndex < 0 ? fields : fields.slice(0, nextTitleIndex);
    },
    [data?.fields],
  );

  useEffect(() => {
    if (
      field.clearWhenHidden &&
      !isCurrentlyVisible &&
      wasVisibleRef.current &&
      field.name
    ) {
      if ((window as any).globalSetFieldValue) {
        const clearValue = field.type === "checkbox" ? false : "";
        (window as any).globalSetFieldValue(field.name, clearValue);
      }
    }

    wasVisibleRef.current = isCurrentlyVisible;
  }, [field.clearWhenHidden, field.name, field.type, isCurrentlyVisible]);

  const collapseItems = useCallback(() => {
    if (!field?.collapsible || !data?.fields) return;

    setCollapsed((prevCollapsed) => {
      const nextCollapsed = !prevCollapsed;

      const collapsibleFields = getFieldsAboveHeading(index);
      const updatedFields = collapsibleFields.map((formField) => ({
        ...formField,
        hide: nextCollapsed,
      }));

      const nextFields = [...data.fields];
      nextFields.splice(index + 1, collapsibleFields.length, ...updatedFields);

      updateFormData({
        ...formData,
        data: {
          ...data,
          fields: nextFields,
        },
      });

      return nextCollapsed;
    });
  }, [field?.collapsible, data, formData, getFieldsAboveHeading, index, updateFormData]);

  useEffect(() => {
    if (
      didInitCollapsedRef.current ||
      field.type !== "title" ||
      !field?.collapsible?.collapsed
    ) {
      return;
    }

    didInitCollapsedRef.current = true;
    collapseItems();
  }, [field.type, field?.collapsible?.collapsed, collapseItems]);

  useEffect(() => {
    if (field.type !== "title" || !data?.fields) return;

    const fieldsAbove = getFieldsAboveHeading(index);
    const hasError = fieldsAbove.some(
      (formField) => formField.name && errors[formField.name],
    );

    const nextErrorValue = hasError && collapsed;
    const currentErrorValue = !!data.fields[index]?.error;

    if (currentErrorValue === nextErrorValue) return;

    const nextFields = [...data.fields];
    nextFields[index] = {
      ...nextFields[index],
      error: nextErrorValue,
    };

    updateFormData({
      ...formData,
      data: {
        ...data,
        fields: nextFields,
      },
    });
  }, [
    collapsed,
    data,
    errors,
    field.type,
    formData,
    getFieldsAboveHeading,
    index,
    updateFormData,
  ]);

  const fieldClass = classNames({
    "tool-form__form__field": true,
    "is-focused": focused,
    [`type-${field.type}`]: field.type,
    "is-collapsible": field.type === "title" && field.collapsible,
    "is-collapsed": field.type === "title" && collapsed,
    "col-span-2": ["title", "heading", "checkbox", "radio"].includes(field.type),
    "col-span-2 md:col-span-1 lg:col-span-2":
      !["title", "heading", "checkbox", "radio"].includes(field.type) &&
      !field.half,
    "col-span-1 md:col-span-2 lg:col-span-1": field.half,
    "col-span-2 md:col-span-2 lg:col-span-2": field.full,
    "tool-form__form__field--error": field.error,
    [`${field.className}`]: field.type === "group" && field.className,
  });

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);

  if (field.hide) return null;

  return (
    <div className={fieldClass}>
      <RenderFieldWithContext
        field={field}
        errors={errors}
        values={values}
        collapseItems={collapseItems}
        onFocus={handleFocus}
        onBlur={handleBlur}
        touched={touched}
        hasSubmitted={hasSubmitted}
      />
    </div>
  );
};

export default FormFieldWithContext;
